import {
  useCallback,
  useMemo,
  ReactNode,
  CSSProperties,
  ChangeEvent,
} from 'react';

import {
  MenuItem,
  FormControl,
  InputLabel,
  Icon,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
} from '@material-ui/core';
import clsx from 'clsx';
import { v4 as uuid } from 'uuid';

import { useStyles } from './styles';

type DataType<T> = T & {
  id: string;
  name: string;
};

type CustomStyles = {
  formControl?: CSSProperties;
};

interface SelectProps<T> extends MuiSelectProps {
  data: DataType<T>[];
  label?: ReactNode;
  value?: DataType<T>;
  onChangeValue(newValue: DataType<T>): void;
  active?: boolean;
  customStyles?: CustomStyles;
}

export function Select<T = any>({
  data,
  value,
  label,
  onChangeValue,
  active,
  customStyles,
  className,
  ...rest
}: SelectProps<T>): JSX.Element {
  const classes = useStyles({ active });
  const componentId = uuid();

  const memoLabel = useMemo(() => {
    return label || null;
  }, [label]);

  const handleChange = useCallback(
    (event: ChangeEvent<{ value: unknown }>) => {
      const { value: selectValue } = event.target;
      const selectedItem = data.find(item => item.id === selectValue);

      onChangeValue(selectedItem as DataType<T>);
    },
    [data, onChangeValue],
  );

  return (
    <FormControl
      variant="outlined"
      className={clsx(classes.formControl, className)}
      style={customStyles?.formControl}
    >
      {memoLabel && (
        <InputLabel
          id={`outlined-label-${componentId}`}
          className={classes.inputLabelSelect}
        >
          {memoLabel}
        </InputLabel>
      )}
      <MuiSelect
        className={classes.selectBrands}
        classes={{ select: classes.select }}
        labelId={`outlined-label-${componentId}`}
        id={`outlined-select-${componentId}`}
        onChange={handleChange}
        value={value ? value.id : value}
        label={memoLabel}
        IconComponent={props => (
          <Icon {...props} className={clsx(props.className, classes.icon)}>
            keyboard_arrow_down
          </Icon>
        )}
        onClose={() => {
          setTimeout(() => {
            (document.activeElement as HTMLSelectElement)?.blur();
          }, 0);
        }}
        MenuProps={{
          autoFocus: false,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
        }}
        {...rest}
      >
        {data.map(item => (
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        ))}
      </MuiSelect>
    </FormControl>
  );
}
